/**
 * Centralized Logging Utility
 *
 * Provides conditional logging based on environment
 * - Development: All logs enabled
 * - Production: Only errors logged
 *
 * Usage:
 *   import logger from '../utils/logger';
 *   logger.info('User logged in', { userId: 123 });
 *   logger.error('API call failed', error);
 */

const isDevelopment = process.env.NODE_ENV === 'development' ||
                      import.meta.env.REACT_APP_ENABLE_LOGGING === 'true';

const isDebugEnabled = import.meta.env.REACT_APP_ENABLE_DEBUG === 'true';

/**
 * Format log message with timestamp
 */
const formatMessage = (level, message, data) => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level}]`;

  if (data) {
    return `${prefix} ${message}`, data;
  }
  return `${prefix} ${message}`;
};

/**
 * Logger utility object
 */
const logger = {
  /**
   * Log informational messages (development only)
   */
  info: (message, ...args) => {
    if (isDevelopment) {
      console.log(...formatMessage('INFO', message, args));
    }
  },

  /**
   * Log debug messages (only when debug enabled)
   */
  debug: (message, ...args) => {
    if (isDebugEnabled) {
      console.log(...formatMessage('DEBUG', message, args));
    }
  },

  /**
   * Log warning messages (development only)
   */
  warn: (message, ...args) => {
    if (isDevelopment) {
      console.warn(...formatMessage('WARN', message, args));
    }
  },

  /**
   * Log error messages (always logged)
   */
  error: (message, ...args) => {
    console.error(...formatMessage('ERROR', message, args));
  },

  /**
   * Log API requests (development only)
   */
  api: (method, url, data = null) => {
    if (isDevelopment) {
      console.log(`[API] ${method} ${url}`, data || '');
    }
  },

  /**
   * Log user actions (for analytics)
   */
  event: (eventName, eventData = {}) => {
    if (isDevelopment) {
      console.log(`[EVENT] ${eventName}`, eventData);
    }
    // In production, this would send to analytics service
    // e.g., analytics.track(eventName, eventData);
  },

  /**
   * Performance logging
   */
  performance: (label, duration) => {
    if (isDebugEnabled) {
      console.log(`[PERF] ${label}: ${duration}ms`);
    }
  },

  /**
   * Group related logs (development only)
   */
  group: (label) => {
    if (isDevelopment && console.group) {
      console.group(label);
    }
  },

  /**
   * End log group (development only)
   */
  groupEnd: () => {
    if (isDevelopment && console.groupEnd) {
      console.groupEnd();
    }
  },

  /**
   * Table display for structured data (development only)
   */
  table: (data) => {
    if (isDevelopment && console.table) {
      console.table(data);
    }
  },
};

/**
 * Create a logger instance for a specific module
 */
export const createModuleLogger = (moduleName) => {
  return {
    info: (message, ...args) => logger.info(`[${moduleName}] ${message}`, ...args),
    debug: (message, ...args) => logger.debug(`[${moduleName}] ${message}`, ...args),
    warn: (message, ...args) => logger.warn(`[${moduleName}] ${message}`, ...args),
    error: (message, ...args) => logger.error(`[${moduleName}] ${message}`, ...args),
  };
};

export default logger;
